import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  Grid,
  IconButton,
  Skeleton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CancelIcon from "@mui/icons-material/Cancel";
import { ShieldCheck } from "lucide-react";
import { useForm } from "react-hook-form";
import { useModalStyles } from "../../common/useModalStyles";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { getPatient, resetPatientActiveBeneficationInsuranceSummaryData, resetPatientInfoObject, savePatientVerificationData, usePatientReducer } from "../../../redux/slice/patient/patientSlice";
import { SingleAlertInfo, fetchAllPayers, setSingleAlertObj } from "../../../redux/slice/commonSlice";
import { AttributionOutlined, VerifiedUserOutlined } from "@mui/icons-material";
import { fullName } from "../../../utils/Utility";
import { INVALID_MBI_MESSAGE, isValidMedicareMbi } from "../../../utils/medicareMbi";
import CustomDropdownField from "../../common/CustomDropdownFiled";
import { fetchPracticeByIdData, usePracticeEditSlice } from "../../../redux/slice/practice/practiceEditSlice";
import { Option } from "../../../redux/slice/agency/AgencySlice";
import { fetchAllCodeTablesValues } from "../../../redux/slice/codetableSlice";
import PDFViewer from "../../common/pdf/PDFViewer";
import { useNavigation } from "../../../hooks/useNavigation";
import PatientBenefitInsuranceDetailsPopup from "./PatientBenefitInsuranceDetailsPopup";

interface PatientEligibilityPopUpProps {
  open: boolean;
  onClose: () => void;
  patientData?: any;
}
interface benefitVerificationForm {
  insuranceType: string | null;
  insurerPayerCode: string | null;
  insurerName?: string | null;
  policy: string | null;
  billingType: string | null;
  provider: string | null;
  npi: string | null;
  practiceType: string | null;
}


const PatientEligibilityPopUp: React.FC<PatientEligibilityPopUpProps> = ({
  open,
  onClose,
  patientData,
}) => {
  const { control, handleSubmit, reset, setValue, getValues, watch } = useForm<benefitVerificationForm>();

  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isDark = theme.palette.mode === "dark";
  const {
    tokens,
    paperSx,
    headerSx,
    iconBoxSx,
    bodySx,
    footerSx,
    closeIconSx,
    dropdownWrapSx,
    primaryBtnSx,
    cancelBtnSx,
  } = useModalStyles();
  const { patientInfoObject, patientVerificationData } = usePatientReducer();
  const { practiceEditInfo } = usePracticeEditSlice();
  const [showPDF, setShowPDF] = useState<{isShow:boolean, patientName: string, fileName: string}>({isShow:false, patientName: "", fileName: ""});
 const { navigate } = useNavigation();
    const [showPatientBenefitInsuranceSummaryHistoryPopup, setShowPatientBenefitInsuranceSummaryHistoryPopup] = useState(false);
    const [bvsid, setBvsId] = useState<number|null>(null);
    const [isLoading, setIsLoading] = useState(true);
const patientNameData = fullName(null, patientInfoObject?.firstName, patientInfoObject?.middleName, patientInfoObject?.lastName)
  useEffect(() => {
    if (patientData?.patientId && patientData?.practiceId) {
      dispatch(getPatient({ patientId: patientData?.patientId?.toString() })).unwrap();
      dispatch(fetchPracticeByIdData(patientData?.practiceId))
      dispatch(fetchAllCodeTablesValues());
      dispatch(fetchAllPayers());

      setValue('insuranceType', insuranceTypeOptions[0].value)
    }
  }, [patientData])

  // useEffect(() => {
  //   if (patientInfoObject?.patientInfo?.length > 0) {
  //     const selectedInsuranceType = watch('insuranceType');
  //     if (selectedInsuranceType) {
  //       setValue('insuranceType', selectedInsuranceType);
  //       const selectedInsuranceData = patientInfoObject?.patientInfo?.find((patient: any) => patient?.infoType === `${selectedInsuranceType}_INSURANCE` && patient?.inactiveDate === null) 
  //       if (selectedInsuranceData?.patientinfoData?.insuranceName && selectedInsuranceData?.patientinfoData?.policy && selectedInsuranceData?.patientinfoData?.insurancePayerCode) {
  //         setValue('insurerPayerCode', selectedInsuranceData?.patientinfoData?.insurancePayerCode);
  //         setValue('insurerName', selectedInsuranceData?.patientinfoData?.insuranceName);
  //         setValue('policy', selectedInsuranceData?.patientinfoData?.policy);
  //       } else {
  //         setValue('insurerPayerCode', null);
  //         setValue('insurerName', null);
  //         setValue('policy', null);
  //       }
  //     } else {
  //       setValue('insurerName', null);
  //       setValue('insurerPayerCode', null);
  //       setValue('policy', null);
  //     }
  //   }
  // }, [patientInfoObject?.patientInfo, watch('insuranceType')])
  useEffect(() => {
    if (patientInfoObject?.patientInfo?.length > 0) {
      const selectedInsuranceType = watch('insuranceType');
      if (selectedInsuranceType) {
        setValue('insuranceType', selectedInsuranceType);
        const selectedInsuranceData = patientInfoObject?.patientInfo?.find((patient: any) => patient?.infoType === `${selectedInsuranceType}_INSURANCE` && patient?.inactiveDate === null);
        if (selectedInsuranceData?.patientinfoData?.insuranceName && selectedInsuranceData?.patientinfoData?.policy && selectedInsuranceData?.patientinfoData?.insurancePayerCode) {
          setValue('insurerPayerCode', selectedInsuranceData?.patientinfoData?.insurancePayerCode);
          setValue('insurerName', selectedInsuranceData?.patientinfoData?.insuranceName);
          setValue('policy', selectedInsuranceData?.patientinfoData?.policy);
          setIsLoading(false);  // Stop loading once the values are set
        } else {
          setValue('insurerPayerCode', null);
          setValue('insurerName', null);
          setValue('policy', null);
          setIsLoading(false);  // Stop loading if no insurance data is found
        }
      } else {
        setValue('insurerName', null);
        setValue('insurerPayerCode', null);
        setValue('policy', null);
        setIsLoading(false);  // Stop loading when no insurance type is selected
      }
    }
  }, [patientInfoObject?.patientInfo, watch('insuranceType')]);
  
  useEffect(() => {
    if (practiceEditInfo?.billingType === "GROUP" ) {
      setValue('npi', practiceEditInfo?.npi ? practiceEditInfo?.npi : '')
    } else if (practiceEditInfo?.billingType === "INDIVIDUAL") {
      const selectedProviderId = watch('provider')
      if (selectedProviderId) {
        const selectedProviderData = practiceEditInfo?.practiceProviders?.find((id: any) => id.providerId === selectedProviderId);
        setValue('npi', selectedProviderData ? selectedProviderData?.npi : '')
      }
    } else {
      setValue('npi', '')
    }
  }, [watch('provider'), practiceEditInfo?.billingType ])

  // const handleEligibilityCheck = async (data: any) => {
  //   const formatDate = (date: any) => {
  //     if (date && typeof date === "object" && date.format) {
  //       return date.format("MM/DD/YYYY");
  //     }
  //     return date;
  //   };

  //   const eligibilityRequest = {
  //     payerCode: data.payerCode,
  //     provider: {
  //       firstName: data.providerFirstName,
  //       middleName: data.providerMiddleName,
  //       lastName: data.providerLastName,
  //       npi: data.providerNpi,
  //     },
  //     subscriber: {
  //       firstName: data.subscriberFirstName,
  //       middleName: data.subscriberMiddleName,
  //       lastName: data.subscriberLastName,
  //       dob: formatDate(data.subscriberDob),
  //       memberID: data.subscriberMemberID,
  //     },
  //     isSubscriberPatient: data.isSubscriberPatient,
  //     doS_StartDate: formatDate(data.doS_StartDate),
  //     doS_EndDate: formatDate(data.doS_EndDate),
  //     requestSource: "RestAPI",
  //     practiceTypeCode: data.practiceTypeCode,
  //     referenceId: data.referenceId,
  //     location: data.location,
  //     includeTextResponse: false,
  //   };

  //   const response = await dispatch(checkPatientEligibilty(eligibilityRequest));
  //   if (response.payload) {
  //     const alertObj: SingleAlertInfo = {
  //       message: "Eligibility check successful",
  //       alertType: "success",
  //     };
  //     dispatch(setSingleAlertObj(alertObj));
  //   } else {
  //     const alertObj: SingleAlertInfo = {
  //       message: "Eligibility check failed",
  //       alertType: "error",
  //     };
  //     dispatch(setSingleAlertObj(alertObj));
  //   }
  //   onClose();
  //   reset();
  // };
  const insuranceTypeOptions = [{ label: 'PRIMARY', value: 'PRIMARY' }, { label: 'SECONDARY', value: 'SECONDARY' }]
  const providerOptions: Option[] = useMemo(() => {
    let options: Option[] = [];
    practiceEditInfo?.practiceProviders?.map((provider: any) => {
      const providerName = fullName(null, provider.firstName, provider.middleName, provider.lastName);
      options.push({
        label: `${providerName} (NPI : ${provider.npi})`,
        value: provider.providerId
      });
    })
    if (options.length === 1) {
      setValue('provider', options[0].value); // Set the default value in the form
    }
    return options;
  }, [practiceEditInfo]);
  
  // const dynamicallyDisplayInsurerPolicy = useMemo(()=>{
  //   if(watch('insurerName')&&watch('insurerPayerCode')){
  //     return `${getValues('insurerName')} (${getValues('policy')})`;
  //   }else{
  //     return (
  //       <span style={{ color: "orange" }}>
  //         {"Please select the valid insurace to run Auto verification."}
  //         <br />
  //         {"( Patient Details > Edit > "}
  //         <span style={{ color: 'Highlight', textDecoration: 'underline', cursor: 'pointer' }} onClick={() =>
  //           navigate('/editPatient', {
  //             state: { patientDetails: { isPatientEdit: true, patientId: patientData?.patientId, pverifyPopup: true } },
  //           })
  //         }>{"Insurance Information"}</span>
  //         {" )"}
  //       </span>
  //     )
  //   }
  // },[watch('insurerName'),watch('insurerPayerCode'),watch('insuranceType'),patientInfoObject.patientInfo])

  const dynamicallyDisplayInsurerPolicy = useMemo(() => {
    if (watch('insurerName') && watch('insurerPayerCode')) {
      return `${getValues('insurerName')} (${getValues('policy')})`;
    } else {
      return (
        <span style={{ color: 'orange' }}>
          {'Please select the valid insurance to run Auto verification.'}
          <br />
          {'( Patient Details > Edit > '}
          <span
            style={{
              color: 'Highlight',
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
            onClick={() =>
              navigate('/editPatient', {
                state: { patientDetails: { isPatientEdit: true, patientId: patientData?.patientId, pverifyPopup: true } },
              })
            }
          >
            {'Insurance Information'}
          </span>
          {' )'}
        </span>
      );
    }
  }, [watch('insurerName'), watch('policy'),watch('insurerPayerCode'), watch('insuranceType'), patientInfoObject?.patientInfo]);
  
  const checkSecondaryInsuranceData = useMemo(() => {
    if (patientInfoObject?.patientInfo?.length > 0) {
      const data = patientInfoObject?.patientInfo?.filter((patient: any) => patient?.infoType === "SECONDARY_INSURANCE" && patient?.inactiveDate === null)
      if (data[0]?.patientinfoData?.insuranceName && data[0]?.patientinfoData?.policy) {
        return false;
      } else {
        return true;
      }
    }
  }, [patientInfoObject?.patientInfo])
  const handleCloseVerificationPopup = () => {
     dispatch(resetPatientInfoObject());
    onClose();
  }
  const onSubmit = async (data: any) => {
    const verificationData = {
      ...data,
      patientId: patientData?.patientId,
      practiceId: patientData?.practiceId,
      practiceType:"81",
      isAutoVerify: false,// CS-1325
    }
    if (verificationData && verificationData?.insurerPayerCode !== null && verificationData?.policy !== null) {
      const selectedInsurance = patientInfoObject?.patientInfo?.find(
        (patient: any) => patient?.infoType === `${data?.insuranceType}_INSURANCE` && patient?.inactiveDate === null
      );
      if (
        selectedInsurance?.patientinfoData?.insuranceCategory === "Medicare" &&
        !isValidMedicareMbi(verificationData.policy)
      ) {
        dispatch(setSingleAlertObj({
          message: patientNameData
            ? `${INVALID_MBI_MESSAGE} Patient: ${patientNameData}.`
            : INVALID_MBI_MESSAGE,
          alertType: "error",
        }));
        return;
      }
      const response = await dispatch(savePatientVerificationData(verificationData))
      if (response.payload) {
        if(response?.payload?.bvsId){
          setShowPatientBenefitInsuranceSummaryHistoryPopup(true);
          //CS-1170 - START
          setBvsId(response?.payload?.parentBvsId)
          //CS-1170 - END
        }else{
          const alertObj: SingleAlertInfo = {
            message: "Automated Eligibility Check is failed due to some error.",
            alertType: "error",
          };
          dispatch(setSingleAlertObj(alertObj));
        }
      }
    }else{
      const alertObj: SingleAlertInfo = {
        message: "Insurer & Policy are required for Eligibility Check. Please check the patient details and update it.",
        alertType: "error",
      };
      dispatch(setSingleAlertObj(alertObj));
    }

  }
return (
  <>
      {showPDF.isShow &&
        <PDFViewer
          isOpen={showPDF.isShow}
          onClose={() => {onClose(); setShowPDF({ isShow: false, patientName: "", fileName: "" })}}
          title={showPDF.patientName}
          fileName={showPDF.fileName}
          base64String={patientVerificationData?.content || ""}
        />
      }
    <PatientBenefitInsuranceDetailsPopup
      open={showPatientBenefitInsuranceSummaryHistoryPopup}
      onClose={() => {navigate('/patientDetails', { state: { patientDetails: { patientId: patientInfoObject?.patientId ,patientName:patientNameData ,dob:patientInfoObject?.birthDate } } }); setShowPatientBenefitInsuranceSummaryHistoryPopup(false); dispatch(resetPatientActiveBeneficationInsuranceSummaryData()); setBvsId(null);}}
      bvsId={bvsid}
    />
      <Dialog
        open={open}
        onClose={handleCloseVerificationPopup}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: paperSx(isMobile) }}
        aria-labelledby="patient-eligibility-dialog-title"
      >
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          <Box sx={headerSx}>
            <Box sx={iconBoxSx(tokens.accent)}>
              <ShieldCheck size={22} color={tokens.accent} strokeWidth={1.8} />
            </Box>
            <Box sx={{ minWidth: 0, pr: 4 }}>
              <Typography
                id="patient-eligibility-dialog-title"
                fontWeight={700}
                sx={{
                  fontSize: "1.1rem",
                  color: tokens.textPrimary,
                  lineHeight: 1.3,
                }}
              >
              Automated Eligibility Check
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.8rem",
                  color: isDark ? "#fb923c" : "#ea580c",
                  mt: 0.75,
                  lineHeight: 1.45,
                }}
              >
               This is an instant eligibility check conducted via our third-party service provider,
               pVerify. For orders involving advanced skin substitutes, please ensure to submit an
               actual IVR in your IVR module for further verification.
              </Typography>
            </Box>
            <IconButton
              onClick={handleCloseVerificationPopup}
              size="small"
              sx={closeIconSx}
              aria-label="Close"
            >
              <CloseIcon sx={{ fontSize: "1rem" }} />
            </IconButton>
          </Box>

          <Box
            sx={{
              ...bodySx,
              minHeight: 0,
              overflowY: "auto",
              overflowX: "hidden",
              scrollbarGutter: "stable",
              "&::-webkit-scrollbar": { width: 6, height: 6 },
              "&::-webkit-scrollbar-track": { background: "transparent" },
              "&::-webkit-scrollbar-thumb": {
                background: theme.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "#CBD5E1",
                borderRadius: 4,
              },
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <AttributionOutlined sx={{ color: tokens.textSecondary }} />
                  <Typography sx={{ color: tokens.accent, fontWeight: 700, fontSize: "0.9rem" }}>
                    Patient:
                  </Typography>
                  <Typography fontWeight={700} sx={{ color: tokens.textPrimary, fontSize: "0.95rem" }}>
                    {`${fullName("", patientInfoObject?.firstName, patientInfoObject?.middleName, patientInfoObject?.lastName)} (DOB: ${patientInfoObject?.birthDate})`}
                  </Typography>
                </Box>
              </Grid>
              {!checkSecondaryInsuranceData && (
                <Grid item xs={12}>
                  <Box
                    display="flex"
                    alignItems={{ sm: "center" }}
                    flexDirection={{ xs: "column", sm: "row" }}
                    gap={2}
                  >
                    <Box sx={{ minWidth: { sm: 160 }, width: { xs: "100%", sm: "auto" } }}>
                      <Typography sx={{ fontWeight: 600, fontSize: "0.8rem", color: tokens.textSecondary }}>
                        Insurance Type:
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1, width: "100%" }}>
                      {patientInfoObject?.patientInfo?.length === 0 ? (
                        <Skeleton variant="rounded" height={56} animation="wave" />
                      ) : (
                        <Box sx={dropdownWrapSx()}>
                          <CustomDropdownField
                            name="insuranceType"
                            control={control}
                            options={insuranceTypeOptions}
                            label=""
                            size="small"
                            rules={{ required: "Insurance Type is required." }}
                          />
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Grid>
              )}
              <Grid item xs={12}>
                <Box
                  display="flex"
                  alignItems={{ sm: "flex-start" }}
                  flexDirection={{ xs: "column", sm: "row" }}
                  gap={2}
                >
                  <Box sx={{ minWidth: { sm: 160 }, width: { xs: "100%", sm: "auto" } }}>
                    <Typography sx={{ fontWeight: 600, fontSize: "0.8rem", color: tokens.textSecondary }}>
                      Insurer & Policy#:
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1, width: "100%" }}>
                    {isLoading ? (
                      <Skeleton variant="rounded" height={56} animation="wave" />
                    ) : (
                      <Typography fontWeight={700} sx={{ color: tokens.textPrimary, fontSize: "0.95rem" }}>
                        {dynamicallyDisplayInsurerPolicy}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box
                  display="flex"
                  alignItems={{ sm: "center" }}
                  flexDirection={{ xs: "column", sm: "row" }}
                  gap={2}
                >
                  <Box sx={{ minWidth: { sm: 160 }, width: { xs: "100%", sm: "auto" } }}>
                    <Typography sx={{ fontWeight: 600, fontSize: "0.8rem", color: tokens.textSecondary }}>
                      Billing Type:
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1, width: "100%" }}>
                    {patientInfoObject?.patientInfo?.length === 0 ? (
                      <Skeleton variant="rounded" height={56} animation="wave" />
                    ) : (
                      <Typography fontWeight={700} sx={{ color: tokens.textPrimary, fontSize: "0.95rem" }}>
                        {practiceEditInfo?.billingType === "INDIVIDUAL"
                          ? `${practiceEditInfo?.billingType} NPI`
                          : `${practiceEditInfo?.billingType} NPI - ${getValues("npi")}`}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Grid>
              {practiceEditInfo?.billingType === "INDIVIDUAL" && (
                <Grid item xs={12}>
                  <Box
                    display="flex"
                    alignItems={{ sm: "center" }}
                    flexDirection={{ xs: "column", sm: "row" }}
                    gap={2}
                  >
                    <Box sx={{ minWidth: { sm: 160 }, width: { xs: "100%", sm: "auto" } }}>
                      <Typography sx={{ fontWeight: 600, fontSize: "0.8rem", color: tokens.textSecondary }}>
                        Provider:
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1, width: "100%" }}>
                      {patientInfoObject?.patientInfo?.length === 0 ? (
                        <Skeleton variant="rounded" height={56} animation="wave" />
                      ) : (
                        <Box sx={dropdownWrapSx()}>
                          <CustomDropdownField
                            name="provider"
                            control={control}
                            options={providerOptions}
                            label=""
                            size="small"
                            rules={{ required: "Provider is required." }}
                          />
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>

          <Box sx={footerSx}>
            <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1.5 }}>
              <Button
                fullWidth
                variant="contained"
                type="submit"
                disabled={
                  isLoading || (!getValues("insurerName") && !getValues("insurerPayerCode"))
                }
                startIcon={<VerifiedUserOutlined sx={{ fontSize: "1rem !important" }} />}
                sx={primaryBtnSx}
              >
                Check Eligibility
              </Button>
              <Button
                fullWidth
                variant="outlined"
                type="button"
                onClick={handleCloseVerificationPopup}
                startIcon={<CancelIcon sx={{ fontSize: "1rem !important" }} />}
                sx={cancelBtnSx}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </Box>
      </Dialog>
    </>
  );
};

export default PatientEligibilityPopUp;
